import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Building2, MapPin, Languages, Film } from "lucide-react";
import { api } from "@/lib/api";
import { MovieCard } from "@/components/MovieCard";
import { RelatedReading } from "@/components/RelatedReading";
import { PageToolbar, UnsplashCredit, usePageTranslation } from "@/components/PageToolbar";

export default function CountryDetail() {
  const { code } = useParams();
  const [country, setCountry] = useState(null);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCountry(null);
    api.get(`/countries/${code}`).then((r) => setCountry(r.data));
    api.get("/cities", { params: { country: code } }).then((r) => setCities(r.data));
  }, [code]);

  const blocks = useMemo(() => (country ? { overview: country.overview } : null), [country]);
  const { lang, tr, busy } = usePageTranslation(blocks, `country-${code}`);

  if (!country) return <main className="max-w-7xl mx-auto px-6 py-24 text-center font-display text-2xl text-[#6b5d51]">Loading…</main>;

  return (
    <main data-testid="country-detail-page">
      <section className="bg-[#171210] text-[#f6f1e7] relative overflow-hidden">
        {country.photo && (
          <img src={country.photo.url} alt={country.photo.alt} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <p className="eyebrow" style={{ color: "#e8b96a" }}>National Cinema Home</p>
          <h1 data-testid="country-name" className="font-display font-bold text-4xl sm:text-6xl mt-2">
            {country.flag} {country.name}
          </h1>
          <p className="font-script text-2xl text-[#e8b96a] mt-2">{country.industry}</p>
          <p className="mt-5 text-lg leading-relaxed max-w-3xl text-[#e9e2d4]">{tr("overview", country.overview)}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <PageToolbar speakText={`${country.name}. ${tr("overview", country.overview)}`} busy={busy} lang={lang} />
          </div>
          {country.photo && <UnsplashCredit photo={country.photo} className="mt-4 !text-[#a99e8d]" />}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          [Film, "Annual Productions", country.annual_films],
          [MapPin, "Key Cities", country.key_cities.join(", ")],
          [Languages, "Film Languages", country.languages.join(", ")],
          [Building2, "Institutions", country.film_bodies.join(" · ")],
        ].map(([Icon, label, value]) => (
          <div key={label} className="bg-[#fbf8f1] border border-[#d8ccb8] p-5">
            <Icon className="w-5 h-5 text-[#7d1a1a]" />
            <p className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-[#8a7c6d] mt-3">{label}</p>
            <p className="font-medium text-sm mt-1">{value}</p>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <h2 className="font-display font-bold text-2xl mb-6 rule-thick pt-4">Essential Films</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {country.movies.map((m) => <MovieCard key={m.id} movie={m} />)}
        </div>
        {country.movies.length === 0 && <p className="text-[#6b5d51]">Films from this country are being indexed.</p>}
      </section>

      {cities.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <h2 className="font-display font-bold text-2xl mb-6 rule-thick pt-4">Film Cities</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cities.map((c) => (
              <Link key={c.id} to={`/city/${c.id}`} data-testid={`country-city-${c.id}`} className="poster-card bg-[#fbf8f1] border border-[#d8ccb8] p-5 group">
                <p className="font-display font-bold text-xl group-hover:text-[#7d1a1a] transition-colors">{c.name}</p>
                <p className="font-script text-lg text-[#b06810] mt-1">{c.tagline}</p>
                <p className="text-sm text-[#6b5d51] mt-2 line-clamp-2">{c.overview}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {country.people.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <h2 className="font-display font-bold text-2xl mb-6 rule-thick pt-4">People of {country.name} Cinema</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {country.people.map((p) => (
              <Link key={p.id} to={`/person/${p.id}`} data-testid={`country-person-${p.id}`} className="poster-card bg-[#fbf8f1] border border-[#d8ccb8] p-5 group">
                <p className="font-display font-bold text-xl group-hover:text-[#7d1a1a] transition-colors">{p.name}</p>
                <p className="text-sm text-[#b06810] mt-1">{p.roles.join(" · ")}</p>
                {p.hall_of_fame && <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#7d1a1a] mt-2">★ Hall of Fame</p>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {country.festivals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <h2 className="font-display font-bold text-2xl mb-6 rule-thick pt-4">Festivals</h2>
          {country.festivals.map((f) => (
            <div key={f.id} className="bg-[#efe7d7] p-5 mb-3">
              <p className="font-display font-semibold text-lg">{f.name} — {f.city}</p>
              <p className="text-sm text-[#6b5d51] mt-1">{f.overview}</p>
            </div>
          ))}
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-10">
        <RelatedReading articles={country.related_articles} />
      </section>
    </main>
  );
}
